/**
 * TransactionRow — én rad i lommebokhistorikken (#156, steg 6e; Stell_Lommebok).
 *
 * Skissen har tittel, en linje under og beløpet til høyre:
 *   «Oppgave godkjent · Tømme oppvasken · Pappa  +15 p».
 * Titler og navn i historikken krever en API-endring (MAPPING.md, PLAN.md 6e):
 * radene har bare id-er og `note`. Til den er avklart, er tittelen typen
 * (`points.reason.*`) og linjen under `note` på en justering. Bare der er
 * notatet skrevet av en voksen; et kjøp i butikken har en intern referanse
 * (`cosmetic:<id>`) i `note`, som ikke er tekst for barnet.
 *
 * Beløpet er grønt (`text-good`) med «+» for opptjente poeng og rødbrunt
 * (`text-bad`) med minustegn for brukte, så fargen aldri er eneste kjennetegn.
 * Justeringer kan ha begge fortegn og følger beløpet.
 *
 * Dagen står i gruppeoverskriften (`groupByDay`), så raden viser ikke tid.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPoints } from '../../i18n/formatters';
import type { PointTransactionRow } from './usePointsQueries';

export type { PointTransactionRow } from './usePointsQueries';

export function TransactionRow({ tx }: { tx: PointTransactionRow }): ReactNode {
  const { t } = useTranslation();
  const isGain = tx.amount > 0;
  const points = formatPoints(tx.amount);
  const note = tx.reason === 'adjustment' ? tx.note : null;
  return (
    <li data-testid="transaction-row" className="flex min-h-13 items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-body-s font-bold text-ink">{t(`points.reason.${tx.reason}`)}</p>
        {note ? <p className="m-0 truncate text-caption text-ink-2">{note}</p> : null}
      </div>
      <span
        data-testid="transaction-row-amount"
        data-sign={isGain ? 'gain' : 'cost'}
        className={`tnum shrink-0 text-base font-extrabold ${isGain ? 'text-good' : 'text-bad'}`}
      >
        {isGain ? t('points.amount.gain', { points }) : t('rewards.points', { points })}
      </span>
    </li>
  );
}
